import { create } from 'zustand';
import { devtools, subscribeWithSelector } from 'zustand/middleware';
import * as Cookies from 'js-cookie';
import { jwtDecode } from 'jwt-decode';

import { AccessLevel } from '../model/Identity';
import AuthEndpoints from '../endpoints/AuthEndpoints';
import { ISessionManager } from '../context';

const LOGIN_REDIRECT = import.meta.env.VITE_APP_LOGIN_REDIRECT || '';
const LOGIN_REDIRECT_CLOSE_AFTER =
    import.meta.env.VITE_APP_LOGIN_REDIRECT_CLOSE_AFTER || '';
const LOGOUT_REDIRECT = import.meta.env.VITE_APP_LOGOUT_REDIRECT || '';
const COOKIE_PREFIX = import.meta.env.VITE_APP_COOKIE_PREFIX || '';

const COOKIE_SUFFIX = '__authentigate_token_sanitized';

interface JWTAccessLevelProvisioning {
    clientId: number;
    accessLevel: AccessLevel;
}

interface Session {
    id: number | null;
    firstName: string | null;
    lastName: string | null;
    username: string | null;
    admin: boolean;
    accessLevels: Array<JWTAccessLevelProvisioning>;
}

interface SessionState {
    loggedIn: boolean;
    latestToken: string;
    flashMessage: string;
    session: Session;
}

interface SessionStore extends SessionState {
    // Actions
    onLoad: () => Promise<void>;
    expired: () => Promise<boolean>;
    logout: (flashMessage?: string) => void;
    
    // Getters (computed properties)
    tokenString: string;
    hasAdmin: boolean;
    username: string;
    fullName: string;
    accessLevels: Array<JWTAccessLevelProvisioning>;
    
    // Permission methods
    hasAccessLevelForAnyone: (accessLevel: AccessLevel) => boolean;
    hasAccessLevelFor: (clientId: number, accessLevel: AccessLevel) => boolean;
    hasScheduleAccessForAnyone: () => boolean;
    hasScheduleAdminFor: (clientId: number) => boolean;
    hasPunchClockAccessForAnyone: () => boolean;
    hasPunchClockAccessFor: (clientId: number) => boolean;
    hasScheduleAccessForClient: (clientId: number) => boolean;
    
    // Internal methods
    _parseJwt: (tokenString: string, notifyLogin?: boolean) => void;
    _performLoginCheck: () => Promise<void>;
    _redirectToLogin: (expired?: boolean) => Promise<boolean>;
    _redirectToLogout: () => void;
    _setCallbacks: (onLoggedIn?: () => void, onLoggedOut?: () => void) => void;
    _onLoggedIn?: () => void;
    _onLoggedOut?: () => void;
}

const DefaultState = (): SessionState => {
    return {
        loggedIn: false,
        latestToken: '',
        flashMessage: '',
        session: {
            id: null,
            firstName: null,
            lastName: null,
            username: null,
            admin: false,
            accessLevels: []
        }
    };
};

const Now = () => new Date().getTime() / 1000;

// Create the store
export const useSessionStore = create<SessionStore>()(
    devtools(
        subscribeWithSelector((set, get) => ({
            // Initial state
            ...DefaultState(),
            
            // Callbacks
            _onLoggedIn: undefined,
            _onLoggedOut: undefined,
            
            // Actions
            onLoad: async () => {
                await get()._performLoginCheck();
            },
            
            expired: async (): Promise<boolean> => {
                return await get()._redirectToLogin(true);
            },
            
            logout: (flashMessage: string = '') => {
                get()._redirectToLogout();
            },
            
            // Getters (computed properties)
            get tokenString() {
                return get().latestToken;
            },
            
            get hasAdmin() {
                return get().loggedIn && get().session.admin;
            },
            
            get username() {
                if (get().loggedIn) {
                    return get().session.username || '';
                } else {
                    return '';
                }
            },
            
            get fullName() {
                const session = get().session;
                return (session.firstName || '') + ' ' + (session.lastName || '');
            },
            
            get accessLevels() {
                return get().session ? get().session.accessLevels : [];
            },
            
            // Permission methods
            hasAccessLevelForAnyone: (accessLevel: AccessLevel): boolean => {
                const session = get().session;
                return (
                    session &&
                    session.accessLevels.findIndex(
                        (al) => al.accessLevel === accessLevel
                    ) >= 0
                );
            },
            
            hasAccessLevelFor: (clientId: number, accessLevel: AccessLevel): boolean => {
                const session = get().session;
                return (
                    session &&
                    session.accessLevels.findIndex(
                        (al) =>
                            al.clientId === clientId && al.accessLevel === accessLevel
                    ) >= 0
                );
            },
            
            hasScheduleAccessForAnyone: (): boolean => {
                return (
                    get().hasAdmin ||
                    get().hasAccessLevelForAnyone(AccessLevel.SCHEDULE_MANAGER) ||
                    get().hasAccessLevelForAnyone(AccessLevel.SCHEDULE_ADMIN) ||
                    get().hasAccessLevelForAnyone(
                        AccessLevel.SCHEDULE_AND_PUNCH_CLOCK_ADMIN
                    )
                );
            },
            
            hasScheduleAdminFor: (clientId: number): boolean => {
                return (
                    get().hasAdmin ||
                    get().hasAccessLevelFor(clientId, AccessLevel.SCHEDULE_ADMIN) ||
                    get().hasAccessLevelFor(
                        clientId,
                        AccessLevel.SCHEDULE_AND_PUNCH_CLOCK_ADMIN
                    )
                );
            },
            
            hasPunchClockAccessForAnyone: (): boolean => {
                return (
                    get().hasAdmin ||
                    get().hasAccessLevelForAnyone(
                        AccessLevel.SCHEDULE_AND_PUNCH_CLOCK_ADMIN
                    )
                );
            },
            
            hasPunchClockAccessFor: (clientId: number): boolean => {
                return (
                    get().hasAdmin ||
                    get().hasAccessLevelFor(
                        clientId,
                        AccessLevel.SCHEDULE_AND_PUNCH_CLOCK_ADMIN
                    )
                );
            },
            
            hasScheduleAccessForClient: (clientId: number): boolean => {
                return (
                    get().hasAdmin ||
                    get().hasAccessLevelFor(clientId, AccessLevel.SCHEDULE_MANAGER) ||
                    get().hasAccessLevelFor(clientId, AccessLevel.SCHEDULE_ADMIN) ||
                    get().hasAccessLevelFor(
                        clientId,
                        AccessLevel.SCHEDULE_AND_PUNCH_CLOCK_ADMIN
                    )
                );
            },
            
            // Internal methods
            _parseJwt: (tokenString: string, notifyLogin: boolean = false) => {
                const token = jwtDecode(tokenString);
                if (token && token.exp - Now() > 0) {
                    set({
                        latestToken: tokenString,
                        loggedIn: true,
                        session: {
                            id: token.id,
                            firstName: token.firstName,
                            lastName: token.lastName,
                            username: token.username,
                            admin: token.admin,
                            accessLevels: token.accessLevels
                        }
                    });
                    if (notifyLogin && get()._onLoggedIn) {
                        get()._onLoggedIn!();
                    }
                } else {
                    set(DefaultState());
                }
            },
            
            _performLoginCheck: async () => {
                if (window.location.pathname.toLowerCase() === '/ems/cne-accreditation' || window.location.pathname.toLowerCase() === '/cne-accreditation') {
                    return;
                }
                const pingOk = await AuthEndpoints.ping();
                if (pingOk) {
                    const cookieToken = Cookies.get(`${COOKIE_PREFIX}${COOKIE_SUFFIX}`);
                    if (!cookieToken) {
                        await get()._redirectToLogin();
                    } else {
                        get()._parseJwt(cookieToken);
                    }
                } else {
                    await get()._redirectToLogin();
                }
            },
            
            _redirectToLogin: async (expired: boolean = false): Promise<boolean> => {
                return new Promise(async (resolve: (success: boolean) => any) => {
                    if (expired) {
                        // Import DialogManager dynamically to avoid circular dependency
                        const { DialogManager } = await import('../context');
                        const tryAgain = await DialogManager.showConfirm(`
Your session has expired. Press OK to open a new window to login again.
Note that you may need to allow popups when asked.

If you're all done, press Cancel to logout.
                        `);
                        if (tryAgain) {
                            const w = window.open(
                                LOGIN_REDIRECT_CLOSE_AFTER,
                                '_blank',
                                'toolbar=no,scrollbars=yes,resizable=yes,top=250,left=250,width=500,height=650'
                            );
                            const timer = setInterval(() => {
                                if (w && w.closed) {
                                    clearInterval(timer);
                                    resolve(true);
                                }
                            }, 300);
                        } else {
                            resolve(false);
                            window.location.href = LOGIN_REDIRECT;
                        }
                    } else {
                        resolve(false);
                        window.location.href = LOGIN_REDIRECT;
                    }
                });
            },
            
            _redirectToLogout: () => {
                window.location.href = LOGOUT_REDIRECT;
            },
            
            _setCallbacks: (onLoggedIn?: () => void, onLoggedOut?: () => void) => {
                set({
                    _onLoggedIn: onLoggedIn,
                    _onLoggedOut: onLoggedOut
                });
            }
        })),
        { name: 'session-store' }
    )
);

// Export a class-like interface that matches the original MobX SessionStore
export class SessionStore implements ISessionManager {
    private store = useSessionStore;
    
    constructor(
        onLoggedIn: () => void = () => true,
        onLoggedOut: () => void = () => true
    ) {
        this.store.getState()._setCallbacks(onLoggedIn, onLoggedOut);
    }
    
    async onLoad() {
        return await this.store.getState().onLoad();
    }
    
    async expired(): Promise<boolean> {
        return await this.store.getState().expired();
    }
    
    logout(flashMessage: string = '') {
        this.store.getState().logout(flashMessage);
    }
    
    get session(): Session {
        return this.store.getState().session;
    }
    
    get accessLevels(): Array<JWTAccessLevelProvisioning> {
        return this.store.getState().accessLevels;
    }
    
    get username(): string {
        return this.store.getState().username;
    }
    
    get loggedIn(): boolean {
        return this.store.getState().loggedIn;
    }
    
    get tokenString(): string {
        return this.store.getState().tokenString;
    }
    
    get hasAdmin(): boolean {
        return this.store.getState().hasAdmin;
    }
    
    get flashMessage(): string {
        return this.store.getState().flashMessage;
    }
    
    get fullName(): string {
        return this.store.getState().fullName;
    }
    
    hasAccessLevelForAnyone(accessLevel: AccessLevel): boolean {
        return this.store.getState().hasAccessLevelForAnyone(accessLevel);
    }
    
    hasAccessLevelFor(clientId: number, accessLevel: AccessLevel): boolean {
        return this.store.getState().hasAccessLevelFor(clientId, accessLevel);
    }
    
    hasScheduleAccessForAnyone(): boolean {
        return this.store.getState().hasScheduleAccessForAnyone();
    }
    
    hasScheduleAdminFor(clientId: number): boolean {
        return this.store.getState().hasScheduleAdminFor(clientId);
    }
    
    hasPunchClockAccessForAnyone(): boolean {
        return this.store.getState().hasPunchClockAccessForAnyone();
    }
    
    hasPunchClockAccessFor(clientId: number): boolean {
        return this.store.getState().hasPunchClockAccessFor(clientId);
    }
    
    hasScheduleAccessForClient(clientId: number): boolean {
        return this.store.getState().hasScheduleAccessForClient(clientId);
    }
}